import React, { useState, useEffect, useContext, useMemo } from 'react';
import { MdDownload, MdOutlineVpnKey } from 'react-icons/md';

import BBITable from '../../helpers/bBITable/BBITable';
import DateOnlyFormatter from '../../helpers/inputs/DateOnlyFormatter';
import Filter from '../../helpers/filter/Filter';
import FilterData from '../../helpers/filter/FilterData';
import Get from '../../api/internal/Get';
import Header from '../../layouts/Header';
import View from '../../helpers/slab/View';
import ViewCustomer from '../../features/views/customer/Customer';
import ViewLoad from '../../features/views/load/Load';
import ViewRental from '../../features/views/rental/Rental';
import ViewReceivable from '../../features/views/receivable/Receivable';
import { BranchContext } from '../../helpers/BranchContext';
import useBranchList from '../../hooks/useBranchList';
import SelectToggle from '../loadboard/SelectToggle';
import { TbRoute } from 'react-icons/tb';
import { CSVLink } from 'react-csv';

function Receivable({ toggleBoardRefresh }) {
  const [filterValue, setFilterValue] = useState('');
  const [needsInvoiceBoard, setNeedsInvoiceBoard] = useState<
    Array<ReceivableBoard>
  >([]);
  const [selectedBranches, setSelectedBranches] = useContext(BranchContext);
  const [branchSelectOpen, setBranchSelectOpen] = useState(false);
  const [viewInvoiceType, setViewInvoiceType] = useState<'load' | 'rental'>(
    'load',
  );
  //only load invoices are branch-specific, rentals are not
  const branchList = useBranchList();

  useEffect(() => {
    function GetNeedsInvoiceBoard() {
      const queryString = GenerateQueryString(
        selectedBranches,
        viewInvoiceType,
      );
      Get(`/Accounting/GetNeedsInvoiceBoard?${queryString}`).then(response => {
        if (response) {
          setNeedsInvoiceBoard(response.data);
        }
      });
    }

    GetNeedsInvoiceBoard();
  }, [toggleBoardRefresh, selectedBranches, viewInvoiceType]);

  const GenerateQueryString = (
    values: string[],
    invoiceType: string,
  ): string => {
    //branchIds are strings due to being stored with cookies
    const intValues = values.map(value => {
      let parsedValue = parseInt(value, 10);
      return isNaN(parsedValue) ? null : parsedValue;
    });
    const validIntValues = intValues.filter(value => value !== null);
    const queryString = validIntValues
      .map(value => `branchId=${value}`)
      .join('&');
    return (
      queryString + (queryString ? '&' : '') + `invoiceType=${invoiceType}`
    );
  };

  const formattedReceivables = useMemo(() => {
    return needsInvoiceBoard.map(receivable => ({
      ...receivable,
      attribute:
        viewInvoiceType === 'load' ? receivable.loadId : receivable.rentalId,
      pickupDate: DateOnlyFormatter(receivable.pickupDate),
      dropOffDate: DateOnlyFormatter(receivable.dropOffDate),
      dueDate: DateOnlyFormatter(receivable.dueDate),
      invoiceDate: DateOnlyFormatter(receivable.invoiceDate),
      total: receivable.total.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    }));
  }, [needsInvoiceBoard, viewInvoiceType]);

  const filteredData = FilterData(formattedReceivables, filterValue);

  const NeedsInvoiceTable = ({ receivableList }) => {
    // Filter data based on viewInvoiceType
    const filteredReceivableList = receivableList.filter(receivable =>
      viewInvoiceType === 'load' ? receivable.loadId : receivable.rentalId,
    );

    const formattedReceivableList = filteredReceivableList.map(receivable => ({
      ...receivable,
      attribute:
        viewInvoiceType === 'load' ? receivable.loadId : receivable.rentalId,
      pickupDate: DateOnlyFormatter(receivable.pickupDate),
      dropOffDate: DateOnlyFormatter(receivable.dropOffDate),
      dueDate: DateOnlyFormatter(receivable.dueDate),
      invoiceDate: DateOnlyFormatter(receivable.invoiceDate),
      total: receivable.total.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    }));

    const columns = [
      {
        key: '1',
        attribute: 'attribute',
        attributeprimaryhidden:
          viewInvoiceType === 'load' ? 'loadId' : 'rentalId',
        header: viewInvoiceType === 'load' ? 'Load #' : 'Rental #',
        onClick: e =>
          View(
            viewInvoiceType === 'load' ? (
              <ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />
            ) : (
              <ViewRental rentalId={e.target.dataset.attributeprimaryhidden} />
            ),
          ),
        dataType: 'number',
      },
      {
        key: '2',
        attribute: 'reference',
        header: 'Reference',
        attributeprimaryhidden:
          viewInvoiceType === 'load' ? 'loadId' : 'rentalId',
        onClick: e =>
          View(
            viewInvoiceType === 'load' ? (
              <ViewLoad loadId={e.target.dataset.attributeprimaryhidden} />
            ) : (
              <ViewRental rentalId={e.target.dataset.attributeprimaryhidden} />
            ),
          ),
      },
      {
        key: '3',
        attribute: 'paymentStatus',
        attributeprimaryhidden: 'customerAssignmentId',
        attributesecondaryhidden: 'loadId',
        attributetertiaryhidden: 'rentalId',
        header: 'Status',
        onClick: e =>
          View(
            <ViewReceivable
              customerAssignmentId={e.target.dataset.attributeprimaryhidden}
              loadId={e.target.dataset.attributesecondaryhidden}
              rentalId={e.target.dataset.attributetertiaryhidden}
            />,
          ),
        dataType: 'string',
      },
      {
        key: '4',
        attribute: 'customerName',
        attributeprimaryhidden: 'customerId',
        header: 'Customer',
        onClick: e =>
          View(
            <ViewCustomer
              customerName={e.target.innerText}
              customerId={e.target.dataset.attributeprimaryhidden}
            />,
          ),
      },
      {
        key: '5',
        attribute: 'pickupDate',
        header: 'Pickup Date',
        dataType: 'date',
      },
      {
        key: '6',
        attribute: 'dropOffDate',
        header: 'Drop Off Date',
        dataType: 'date',
      },
      ...(viewInvoiceType === 'load'
        ? [
            {
              key: '7',
              attribute: 'branchName',
              header: 'Branch',
            },
          ]
        : []),
      {
        key: '8',
        attribute: 'invoiceDate',
        header: 'Invoice Date',
        dataType: 'date',
      },
      {
        key: '9',
        attribute: 'dueDate',
        header: 'Due Date',
        dataType: 'date',
      },
      {
        key: '10',
        attribute: 'total',
        header: 'Total',
        dataType: 'currency',
      },
    ];
    return <BBITable data={formattedReceivableList ?? []} columns={columns} />;
  };

  const DownloadCsvButton = () => {
    const csvFileName =
      new Date().toISOString().split('T')[0].replace(/[-]/g, '') +
      '_Needs Invoice Report.csv';

    const csvHeaders = [
      {
        label: viewInvoiceType === 'load' ? 'Load #' : 'Rental #',
        key: viewInvoiceType === 'load' ? 'loadId' : 'rentalId',
      },
      { label: 'Reference', key: 'reference' },
      { label: 'Status', key: 'paymentStatus' },
      { label: 'Customer', key: 'customerName' },
      { label: 'Pickup Date', key: 'pickupDate' },
      { label: 'Drop Off Date', key: 'dropOffDate' },
      ...(viewInvoiceType === 'load'
        ? [{ label: 'Branch', key: 'branchName' }]
        : []),
      { label: 'Invoice Date', key: 'invoiceDate' },
      { label: 'Due Date', key: 'dueDate' },
      { label: 'Total', key: 'total' },
    ];

    return (
      <button>
        <CSVLink
          data={filteredData}
          headers={csvHeaders}
          filename={csvFileName}
        >
          <MdDownload />
          Download
        </CSVLink>
      </button>
    );
  };

  return (
    <>
      <h1>Needs Invoice</h1>
      <Header>
        <div>
          <button
            className={viewInvoiceType === 'load' ? 'active' : ''}
            onClick={() => setViewInvoiceType('load')}
          >
            <TbRoute title="Load" />
            Loads
          </button>
          <button
            className={viewInvoiceType === 'rental' ? 'active' : ''}
            onClick={() => setViewInvoiceType('rental')}
          >
            <MdOutlineVpnKey title="Rental" />
            Rentals
          </button>
          <DownloadCsvButton />
        </div>
        <div>
          {viewInvoiceType === 'load' && (
            <SelectToggle
              title="Branch"
              isOpen={branchSelectOpen}
              setIsOpen={setBranchSelectOpen}
              selectedItems={selectedBranches}
              setSelectedItems={setSelectedBranches}
              options={branchList}
              cookieKey="branches"
              idKey="branchId"
              nameKey="branchName"
            />
          )}
          <Filter
            filterValue={filterValue}
            setFilterValue={setFilterValue}
            placeholder="Filter receivables"
          />
        </div>
      </Header>
      {filteredData && <NeedsInvoiceTable receivableList={filteredData} />}
    </>
  );
}

export default Receivable;
